import { makeApiRequest } from './api';

/**
 * Platform reference data, served from `GET /reference-data`.
 *
 * This replaces the option lists that used to be typed by hand into onboarding
 * and settings components. There were three separate industry vocabularies in
 * the repo that did not agree with each other, and 145 lines of Singapore
 * geography in a form that never asked which country the customer was in.
 *
 * Rendering rule: never print `label` directly when a translation exists. Use
 * `referenceLabel(t, kind, item)` so an admin-added row still shows sensibly
 * without a translation key.
 */

export type ReferenceBandKind =
  | 'revenue'
  | 'ad_spend'
  | 'order_value'
  | 'store_count';

export interface IReferenceItem {
  id: string;
  code: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
}

export interface IIndustry extends IReferenceItem {
  /** IAB tier-1 code used when provisioning the advertiser account. */
  iabCode?: string;
}

export interface IBusinessType extends IReferenceItem {
  /** ISO 3166-1 alpha-2, or null for a legal form offered everywhere. */
  countryCode?: string | null;
}

export type IConsumerGroup = IReferenceItem;

export interface IPlatformCurrency {
  id: string;
  code: string;
  name: string;
  symbol?: string;
  isEnabled: boolean;
  sortOrder: number;
}

export interface IReferenceBand {
  id: string;
  kind: ReferenceBandKind;
  code: string;
  label?: string;
  /**
   * Money bands carry USD **minor units** (cents); `store_count` carries plain
   * integers. Postgres returns bigint as a string, hence the union.
   */
  rangeMin?: string | number | null;
  rangeMax?: string | number | null;
  sortOrder: number;
  isActive: boolean;
}

export interface ITermsVersion {
  id: string;
  version: string;
  body: string;
  effectiveFrom: string;
  isActive: boolean;
}

export interface IReferenceDataBundle {
  industries: IIndustry[];
  businessTypes: IBusinessType[];
  consumerGroups: IConsumerGroup[];
  currencies: IPlatformCurrency[];
  bands: Record<ReferenceBandKind, IReferenceBand[]>;
  termsVersion: ITermsVersion | null;
}

/**
 * The API wraps every response as `{ status, success, message, data }`, where
 * `data` holds the controller's payload with `message` lifted out. The bundle is
 * spread at the top level of that payload, so `response.data` *is* the bundle.
 */
interface ReferenceDataResponse {
  success: boolean;
  message: string;
  data: IReferenceDataBundle;
}

export const getReferenceDataService = async (
  countryCode?: string,
  token?: string
): Promise<IReferenceDataBundle | null> => {
  const query = countryCode
    ? `?country=${encodeURIComponent(countryCode)}`
    : '';
  const response = await makeApiRequest<ReferenceDataResponse>(
    `/reference-data${query}`,
    { method: 'GET', token }
  );
  return response?.data ?? null;
};

interface TermsResponse {
  success: boolean;
  message: string;
  data: { terms: ITermsVersion | null };
}

export const getActiveTermsService = async (
  token?: string
): Promise<ITermsVersion | null> => {
  const response = await makeApiRequest<TermsResponse>('/reference-data/terms', {
    method: 'GET',
    token,
  });
  return response?.data?.terms ?? null;
};

export interface IProfileRequirement {
  field: string;
  section: 'business-profile' | 'marketing-profile' | 'legal';
}

export interface IBusinessProfileCompleteness {
  isComplete: boolean;
  missingRequired: IProfileRequirement[];
  missingRecommended: IProfileRequirement[];
  completedCount: number;
  totalTracked: number;
}

interface CompletenessResponse {
  success: boolean;
  message: string;
  data: { completeness: IBusinessProfileCompleteness };
}

/** What still has to be filled in before this business can launch a campaign. */
export const getBusinessProfileCompletenessService = async (
  businessId: string,
  token?: string
): Promise<IBusinessProfileCompleteness | null> => {
  const response = await makeApiRequest<CompletenessResponse>(
    `/businesses/${businessId}/profile-completeness`,
    { method: 'GET', token }
  );
  return response?.data?.completeness ?? null;
};

export interface ITermsAcceptance {
  id: string;
  userId: string;
  businessId: string;
  termsVersionId: string;
  signatureName?: string;
  acceptedAt: string;
}

interface TermsAcceptanceResponse {
  success: boolean;
  message: string;
  data: { acceptance: ITermsAcceptance | null };
}

export const acceptTermsService = async (
  businessId: string,
  signatureName: string,
  token?: string
): Promise<ITermsAcceptance | null> => {
  const response = await makeApiRequest<TermsAcceptanceResponse>(
    `/businesses/${businessId}/terms-acceptance`,
    {
      method: 'POST',
      body: JSON.stringify({ signatureName }),
      token,
    }
  );
  return response?.data?.acceptance ?? null;
};

export const getTermsAcceptanceService = async (
  businessId: string,
  token?: string
): Promise<ITermsAcceptance | null> => {
  const response = await makeApiRequest<TermsAcceptanceResponse>(
    `/businesses/${businessId}/terms-acceptance`,
    { method: 'GET', token }
  );
  return response?.data?.acceptance ?? null;
};
